import { useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import CostLineRow from "@/components/admin/production/CostLineRow"
import VariantRow from "@/components/admin/production/VariantRow"
import AssetPicker from "@/components/admin/production/AssetPicker"
import type {
  AssetOption,
  AssetUsageRow,
  BatchVariantRow,
  CostLineRow as CostLineRowData,
  VariantOption,
} from "@/components/admin/production/types"

interface BatchFormState {
  label: string
  produced_on: string
  notes: string
  variants: BatchVariantRow[]
  cost_lines: CostLineRowData[]
  asset_usages: AssetUsageRow[]
}

interface BatchFormProps {
  initial?: Partial<BatchFormState>
  item_variants: VariantOption[]
  active_assets: AssetOption[]
  cost_kinds: string[]
  submitUrl: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
  cancelUrl: string
}

export default function BatchForm({
  initial,
  item_variants,
  active_assets,
  cost_kinds,
  submitUrl,
  method,
  errors,
  cancelUrl,
}: BatchFormProps) {
  const [state, setState] = useState<BatchFormState>({
    label: initial?.label ?? "",
    produced_on: initial?.produced_on ?? new Date().toISOString().slice(0, 10),
    notes: initial?.notes ?? "",
    variants: initial?.variants ?? [],
    cost_lines: initial?.cost_lines ?? [],
    asset_usages: initial?.asset_usages ?? [],
  })

  const addVariant = () =>
    setState((s) => ({
      ...s,
      variants: [...s.variants, { item_variant_id: "", units_produced: "" }],
    }))

  const setVariant = (index: number, patch: Partial<BatchVariantRow>) =>
    setState((s) => ({
      ...s,
      variants: s.variants.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }))

  const removeVariant = (index: number) =>
    setState((s) => ({
      ...s,
      variants: s.variants.map((r, i) =>
        i === index ? { ...r, _destroy: true } : r
      ),
    }))

  const addCostLine = () =>
    setState((s) => ({
      ...s,
      cost_lines: [
        ...s.cost_lines,
        { kind: cost_kinds[0] ?? "consumable", label: "", amount_cents: "" },
      ],
    }))

  const setCostLine = (index: number, patch: Partial<CostLineRowData>) =>
    setState((s) => ({
      ...s,
      cost_lines: s.cost_lines.map((r, i) =>
        i === index ? { ...r, ...patch } : r
      ),
    }))

  const removeCostLine = (index: number) =>
    setState((s) => ({
      ...s,
      cost_lines: s.cost_lines.map((r, i) =>
        i === index ? { ...r, _destroy: true } : r
      ),
    }))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const batchData = {
      label: state.label,
      produced_on: state.produced_on,
      notes: state.notes,
      production_batch_variants_attributes: state.variants.map((v) => ({
        id: v.id,
        item_variant_id: v.item_variant_id,
        units_produced: v.units_produced,
        _destroy: v._destroy,
      })),
      production_cost_lines_attributes: state.cost_lines.map((l) => ({
        id: l.id,
        kind: l.kind,
        label: l.label,
        amount_cents: l.amount_cents,
        supplier_name: l.supplier_name,
        quantity: l.quantity,
        unit: l.unit,
        _destroy: l._destroy,
      })),
      production_asset_usages_attributes: state.asset_usages.map((u) => ({
        id: u.id,
        production_asset_id: u.production_asset_id,
        _destroy: u._destroy,
      })),
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = { production_batch: batchData } as any
    if (method === "post") {
      router.post(submitUrl, payload)
    } else {
      router.patch(submitUrl, payload)
    }
  }

  const errorMessages = errors ? Object.entries(errors) : []

  return (
    <form onSubmit={submit} className="space-y-8">
      {errorMessages.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <ul className="list-disc pl-5 space-y-1">
            {errorMessages.map(([field, msgs]) => (
              <li key={field}>
                <strong>{field}</strong>: {(msgs as string[]).join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Informations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              value={state.label}
              onChange={(e) => setState({ ...state, label: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="produced_on">Date de production</Label>
            <Input
              id="produced_on"
              type="date"
              value={state.produced_on}
              onChange={(e) =>
                setState({ ...state, produced_on: e.target.value })
              }
              required
            />
          </div>
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={state.notes}
            onChange={(e) => setState({ ...state, notes: e.target.value })}
            rows={3}
          />
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Variantes produites</h2>
          <Button type="button" variant="outline" size="sm" onClick={addVariant}>
            + Ajouter une variante
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Variante</TableHead>
              <TableHead>Unités</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {state.variants.map((row, idx) => (
              <VariantRow
                key={idx}
                row={row}
                index={idx}
                variants={item_variants}
                onChange={setVariant}
                onRemove={removeVariant}
              />
            ))}
          </TableBody>
        </Table>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Coûts directs</h2>
          <Button type="button" variant="outline" size="sm" onClick={addCostLine}>
            + Ajouter une ligne
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Libellé</TableHead>
              <TableHead>Montant (€)</TableHead>
              <TableHead>Fournisseur</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {state.cost_lines.map((row, idx) => (
              <CostLineRow
                key={idx}
                row={row}
                index={idx}
                kinds={cost_kinds}
                onChange={setCostLine}
                onRemove={removeCostLine}
              />
            ))}
          </TableBody>
        </Table>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Équipements amortis</h2>
        <AssetPicker
          rows={state.asset_usages}
          assets={active_assets}
          onChange={(rows) => setState({ ...state, asset_usages: rows })}
        />
      </section>

      <div className="flex items-center gap-3">
        <Button type="submit">Enregistrer</Button>
        <a
          href={cancelUrl}
          className="text-sm text-gray-500 hover:text-gray-900"
        >
          Annuler
        </a>
      </div>
    </form>
  )
}
